import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";

const KEY = "ek_offline_queue";
const ENDPOINT = { incident: "/incidents", calendar: "/calendar/events" };

const OfflineContext = createContext(null);
export const useOffline = () => useContext(OfflineContext);

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const save = (q) => localStorage.setItem(KEY, JSON.stringify(q));

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState(load());
  const flushing = useRef(false);
  const listeners = useRef(new Set());

  const onSynced = useCallback((cb) => { listeners.current.add(cb); return () => listeners.current.delete(cb); }, []);
  const emitSynced = () => listeners.current.forEach((cb) => { try { cb(); } catch {} });

  const flush = useCallback(async () => {
    if (flushing.current) return;
    const queue = load();
    if (!navigator.onLine || queue.length === 0) return;
    flushing.current = true;
    setSyncing(true);
    let remaining = [...queue];
    let synced = 0;
    for (const item of queue) {
      try {
        await api.post(ENDPOINT[item.kind], item.payload);
        remaining = remaining.filter((x) => x.id !== item.id);
        save(remaining);
        setPending([...remaining]);
        synced += 1;
      } catch (e) {
        if (e?.response) {
          // Server rejected it (validation/auth) — drop so it doesn't loop forever.
          remaining = remaining.filter((x) => x.id !== item.id);
          save(remaining);
          setPending([...remaining]);
          toast.error("An offline record couldn't be saved and was removed.");
        } else {
          break; // network still down — stop and retry later
        }
      }
    }
    flushing.current = false;
    setSyncing(false);
    if (synced > 0) { toast.success(`Synced ${synced} offline ${synced === 1 ? "record" : "records"}`); emitSynced(); }
  }, []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) flush();
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [flush]);

  // Queue an item, or send immediately if online.
  const submit = useCallback(async ({ kind, payload }) => {
    if (navigator.onLine) {
      try {
        const { data } = await api.post(ENDPOINT[kind], payload);
        return { ok: true, data };
      } catch (e) {
        if (e?.response) return { ok: false, error: e };
        // network error -> fall through to queue
      }
    }
    const item = { id: crypto.randomUUID(), kind, payload, ts: Date.now() };
    const q = [...load(), item];
    save(q); setPending(q);
    return { ok: true, queued: true, item };
  }, []);

  return (
    <OfflineContext.Provider value={{ online, syncing, pending, pendingCount: pending.length, submit, onSynced, flush }}>
      {children}
    </OfflineContext.Provider>
  );
}
